import { Minus, Package, Plus, Trash2, Truck } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useUpdateCart } from '~/api';
import type { OrderItem, Organization } from '~/graphql/graphql';
import { calcItemLoads } from '~/utils/order';
import { useDebounce } from '~/hooks/useDebounce';
import type { RootState } from '~/redux/store';
import { extractGraphQLErrorMessages } from '~/utils';
import { unitsPerPiece } from '~/utils/unitConversion';

export type CartProps = {
  organization: Organization;
  items: OrderItem[];
};

function calcUnitsPerBundle(item: OrderItem): number {
  const ppl = item.platformProductsLengths;
  const p = ppl.platformProduct;
  if (p.piecesPerBundle.value == 0) return 0;

  const unit = item.quantity.unit ?? 'MBF';
  const widthInches = p.isNominal
    ? (p.nominalWidthInches ?? p.actualWidthInches ?? 0)
    : (p.actualWidthInches ?? 0);
  const thicknessInches = p.isNominal
    ? (p.nominalThicknessInches ?? p.actualThicknessInches ?? 0)
    : (p.actualThicknessInches ?? 0);
  const lengthInches = p.isNominal
    ? (ppl.length.nominalLengthInches ?? ppl.length.actualLengthInches ?? 0)
    : (p.actualLengthInches ?? ppl.length.actualLengthInches ?? 0);

  if (lengthInches <= 0 || widthInches <= 0 || thicknessInches <= 0) return 0;

  return (
    p.piecesPerBundle.value *
    unitsPerPiece(unit, lengthInches, widthInches, thicknessInches)
  );
}

export function CartCard({ organization, items }: Readonly<CartProps>) {
  const [quantityOverrides, setQuantityOverrides] = useState<
    Record<string, number>
  >({});

  const handleQuantityOverride = (productId: string, value: number) => {
    setQuantityOverrides((prev) => ({ ...prev, [productId]: value }));
  };

  const { truckLoads, railcarLoads } = items.reduce(
    (acc, item) => {
      const productId = item.platformProductsLengths?.id ?? '';
      const overriddenItem =
        productId in quantityOverrides
          ? {
              ...item,
              quantity: {
                ...item.quantity,
                value: quantityOverrides[productId],
              },
            }
          : item;
      const loads = calcItemLoads(overriddenItem);

      return {
        truckLoads: acc.truckLoads + loads.truckLoads,
        railcarLoads: acc.railcarLoads + loads.railcarLoads,
      };
    },
    { truckLoads: 0, railcarLoads: 0 }
  );

  const trucks = truckLoads.toFixed(2);
  const railcars = railcarLoads.toFixed(2);

  const { t } = useTranslation(['order-builder', 'cart']);
  return (
    <div className="bg-neut-light border border-neut-border rounded-lg overflow-hidden">
      <div className="bg-neut-50 px-3 py-2 border-b border-neut-border">
        <div className="flex flex-col gap-1.5">
          <p className="text-xs font-semibold text-neut-900">
            {organization.name}
          </p>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 px-2 py-1 bg-status-info-50 rounded-md">
              <Truck
                size={10}
                className="text-status-info-600"
                aria-hidden="true"
              />
              <span
                data-testid="cart-trucks"
                className="text-xs font-semibold text-status-info-800"
              >
                {trucks}
              </span>
              <span className="text-xs text-status-info-600">
                {t('cart:cart.trucks_lowercase')}
              </span>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 bg-warning-50 rounded-md">
              <Package
                size={10}
                className="text-warning-600"
                aria-hidden="true"
              />
              <span
                data-testid="cart-railcars"
                className="text-xs font-semibold text-warning-700"
              >
                {railcars}
              </span>
              <span className="text-xs text-warning-600">
                {t('cart:cart.railcars_lowercase')}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="divide-y divide-neut-100">
        {[...items]
          .sort((a, b) => {
            const nameA =
              a.platformProductsLengths?.platformProduct?.name ?? '';
            const nameB =
              b.platformProductsLengths?.platformProduct?.name ?? '';
            if (nameA !== nameB) return nameA.localeCompare(nameB);
            const lenA =
              a.platformProductsLengths?.length?.actualLengthInches ?? 0;
            const lenB =
              b.platformProductsLengths?.length?.actualLengthInches ?? 0;
            return lenA - lenB;
          })
          .map((item) => (
            <CartItem
              key={item.platformProductsLengths?.id}
              length={item.platformProductsLengths?.length?.displayValue || ''}
              name={item.platformProductsLengths?.platformProduct?.name || ''}
              pricePerUnit={item.price}
              quantity={item.quantity.value}
              unit={item.quantity.unit || ''}
              sellingOrganizationId={organization.id}
              productId={item.platformProductsLengths?.id || ''}
              onQuantityChange={handleQuantityOverride}
              unitsPerBundle={calcUnitsPerBundle(item)}
              t={t}
            />
          ))}
      </div>
    </div>
  );
}

type CartItemProps = {
  length: string;
  name: string;
  pricePerUnit: number;
  quantity: number;
  unit: string;
  sellingOrganizationId: string;
  productId: string;
  onQuantityChange: (productId: string, value: number) => void;
  unitsPerBundle: number;
  t: (key: string) => string;
};

function CartItem({
  length,
  name,
  pricePerUnit,
  quantity,
  unit,
  sellingOrganizationId,
  productId,
  onQuantityChange,
  unitsPerBundle,
  t,
}: Readonly<CartItemProps>) {
  const [quantityValue, setQuantityValue] = useState(quantity);
  const [inputValue, setInputValue] = useState(quantity.toFixed(3));

  useEffect(() => {
    setQuantityValue(quantity);
    setInputValue(quantity.toFixed(3));
  }, [quantity]);

  const step =
    unitsPerBundle > 0
      ? Math.round(Math.ceil(1 / unitsPerBundle) * unitsPerBundle * 1000) / 1000
      : 1;

  const roundToBundle = (value: number): number => {
    if (unitsPerBundle <= 0) return value;
    return Math.ceil(value / unitsPerBundle) * unitsPerBundle;
  };

  const roundToBundleDown = (value: number): number => {
    if (unitsPerBundle <= 0) return value;
    return Math.floor(value / unitsPerBundle) * unitsPerBundle;
  };
  const [isDeleting, setIsDeleting] = useState(false);
  const { mutate: updateCart } = useUpdateCart();

  const locationId = useSelector(
    (state: RootState) => state.cart.selectedLocationId
  );

  const handleAddToCart = (productId: string, quantityToAdd: string) => {
    updateCart(
      {
        sellingOrganizationId: sellingOrganizationId,
        productId: productId,
        locationId,
        quantity: { value: Number(quantityToAdd), unit: 'MBF' },
      },
      {
        onSettled: () => setIsDeleting(false),
        onError: (error) => {
          setQuantityValue(quantity);
          setInputValue(quantity.toFixed(3));
          onQuantityChange(productId, quantity);
          toast.error(
            t('order_builder.cart_item.cart_update_error') +
              extractGraphQLErrorMessages(error).join(', ')
          );
        },
        onSuccess: () => {
          toast.success(t('order_builder.cart_item.cart_update_success'));
        },
      }
    );
  };

  const debouncedAddToCart = useDebounce(handleAddToCart, 1000);

  const handleQuantityChange = (newValue: number) => {
    newValue = newValue < 0 ? 0 : newValue;
    setQuantityValue(newValue);
    setInputValue(newValue.toFixed(3));
    onQuantityChange(productId, newValue);
    debouncedAddToCart(productId, newValue.toString());
  };

  const handleDelete = () => {
    setIsDeleting(true);
    handleQuantityChange(0);
  };

  return (
    <div className="p-3">
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="mb-1">
            <span className="inline-flex items-center px-2 py-0.5 bg-success-50 text-success-700 rounded text-xs font-medium">
              {length}
            </span>
          </div>
          <p data-testid="cart-item-name" className="text-xs text-neut-700">
            {name}
          </p>
        </div>
        <button
          data-testid="cart-item-delete-button"
          onClick={handleDelete}
          className="p-1 hover:bg-neut-100 rounded transition-colors ml-2 flex gap-2 cursor-pointer"
          disabled={isDeleting}
        >
          <Trash2
            className={`w-3.5 h-3.5 ${isDeleting ? 'text-error-600' : 'text-neut-400 '} hover:text-error-600`}
            aria-hidden="true"
          />
          {isDeleting && (
            <span
              data-testid="cart-item-deleting"
              className="text-xs text-error-600 ml-1"
            >
              {t('order_builder.cart_item.deleting')}
            </span>
          )}
        </button>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            data-testid={`cart-quantity-decrease-button`}
            onClick={() =>
              handleQuantityChange(roundToBundleDown(quantityValue - step))
            }
            className="w-7 h-7 rounded border border-neut-300 flex items-center justify-center hover:bg-neut-50 transition-colors text-neut-600 cursor-pointer"
            disabled={isDeleting}
          >
            <Minus className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
          <input
            data-testid={`cart-quantity-input`}
            type="text"
            inputMode="decimal"
            className="w-16 h-7 text-center text-xs border border-neut-300 rounded focus:outline-none focus:ring-1 focus:ring-prim focus:border-prim"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={(e) =>
              handleQuantityChange(
                roundToBundle(parseFloat(e.target.value) || 0)
              )
            }
            disabled={isDeleting}
          />
          <button
            data-testid={`cart-quantity-increase-button`}
            onClick={() =>
              handleQuantityChange(roundToBundle(quantityValue + step))
            }
            className="w-7 h-7 rounded border border-neut-300 flex items-center justify-center hover:bg-neut-50 transition-colors text-neut-600 cursor-pointer"
            disabled={isDeleting}
          >
            <Plus className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
          <span className="text-xs text-neut-sec">{unit}</span>
        </div>
        <div className="text-right">
          <p
            data-testid={`cart-price-per-unit`}
            className="text-xs text-neut-500"
          >
            ${pricePerUnit?.toLocaleString()}/{unit}
          </p>
          <p
            data-testid={`cart-total-price`}
            className="text-sm font-semibold text-neut-900"
          >
            ${(pricePerUnit * quantityValue)?.toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}
